import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { analytics } from '@/utils/analytics';
import { useAuth } from '@/hooks/useAuth';
import { metaPixel } from '@/utils/meta-pixel';

/**
 * Hook for tracking page views and user events with Google Analytics 4
 */
export const useAnalytics = () => {
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  
  // Initialize analytics on mount
  useEffect(() => {
    analytics.init({
      debugMode: process.env.NODE_ENV === 'development',
      consentGiven: true // You can integrate with cookie consent here
    });
  }, []);
  
  // Track page views on route changes
  useEffect(() => {
    // Délai pour permettre à react-helmet-async de mettre à jour le titre
    const trackPageView = () => {
      analytics.trackPageView({
        page_title: document.title,
        page_location: window.location.href,
        page_path: location.pathname,
        referrer: document.referrer
      });
      
      // Track page view with Meta Pixel
      metaPixel.trackPageView(document.title);
    };
    
    // Délai de 100ms pour s'assurer que le titre est mis à jour
    const timer = setTimeout(trackPageView, 100);
    
    return () => clearTimeout(timer);
  }, [location.pathname]);
  
  // Track user authentication state
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      analytics.setUserId(user.id);
      
      // Set user properties for better segmentation
      if (typeof window.gtag === 'function') {
        window.gtag('config', 'G-JP99R58XZ2', {
          custom_map: {
            user_type: user.user_type || 'individual',
            subscription_status: user.subscription_status || 'free'
          }
        });
      }
    } else {
      analytics.clearUserId();
    }
  }, [isAuthenticated, user]);
  
  return analytics;
};

export default useAnalytics;